import { useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { ClipboardPen, Copy } from "lucide-react";
import { AccountStub } from "protogen/common_pb";
import { PhoneCall, PhoneCall_State } from "protogen/conversation_pb";
import {
  SuggestionEntityType,
  Suggestions,
  SummarySuggestion,
} from "protogen/suggestions_pb";
import { Note } from "protogen/notes_pb";
import { useRunWorkflow } from "services/workflow";
import { SuggestionsList } from "components/activity/Suggestions";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "components/activity/SuggestionActions";
import Spinner from "components/common/Spinner";
import SnackPack, { SnackbarMessage } from "components/common/SnackPack";
import CopyToButton from "components/documents/CopyToButton";
import NotesEditor, { Handle as NotesEditorHandle } from "./NotesEditor";
import DelegationTaskDetails from "./DelegationTaskDetails";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";

type SuggestionsSectionProps = {
  phoneCall: PhoneCall;
  suggestions: Suggestions;
  familyRef: string;
  title: string;
  delegationTaskRef?: string;
  notesEditorRef: React.RefObject<NotesEditorHandle | null>;
  isNotesEmpty: boolean;
};

const Loader = () => (
  <Box
    display="flex"
    flexDirection="row"
    gap="10px"
    alignItems="center"
    sx={{
      width: "100%",
      border: "1px solid #D4D4D4",
      borderRadius: "12px",
      padding: "16px 12px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
    }}
  >
    <Spinner size={20} />
    <Typography color="#198282" variant="body" fontWeight={600}>
      Analyzing call
    </Typography>
  </Box>
);

type SummarySectionProps = {
  summary: SummarySuggestion;
  notesEditorRef: React.RefObject<NotesEditorHandle | null>;
  isNotesEmpty: boolean;
};

const SummarySection = ({
  summary,
  notesEditorRef,
  isNotesEmpty,
}: SummarySectionProps) => {
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);

  const normalizedContent = summary.content
    .replace(/[\s\W]/g, "")
    .toLowerCase();
  const isNoTranscript = normalizedContent === "notranscriptavailable";

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(summary.content);
    setSnackPack([
      { key: Date.now().toString(), message: "Copied to clipboard" },
    ]);
  };

  const handleCopyToNotes = () => {
    notesEditorRef.current?.insertSuggestion({
      text: summary.content.trim(),
    });
    setSnackPack([
      { key: Date.now().toString(), message: "Summary added to notes" },
    ]);
  };

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Summary</Typography>
        {!isNoTranscript && (
          <Box display="flex" gap="4px">
            {isNotesEmpty && (
              <Tooltip title="Copy to notes">
                <IconButton onClick={handleCopyToNotes} size="small">
                  <ClipboardPen size={18} color="#6B6E7B" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyToClipboard} size="small">
                <Copy size={18} color="#6B6E7B" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Typography
        variant="body"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "pre-line",
        }}
      >
        {summary.content}
      </Typography>
      <SnackPack
        snackPack={snackPack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

const SuggestionsSection = ({
  phoneCall,
  suggestions,
  familyRef,
  delegationTaskRef,
  notesEditorRef,
  isNotesEmpty,
}: SuggestionsSectionProps) => {
  const actionsRef = useRef<SuggestionActionsHandle | null>(null);
  const { request: workflowRequest, loading } = useRunWorkflow();
  const isFreshlyCompleteWithoutSuggestions =
    phoneCall.state === PhoneCall_State.COMPLETED &&
    // Updated within the last 30 seconds
    Date.now() - Number(phoneCall.lastUpdatedSec) * 1000 < 30000 &&
    !suggestions.hasSuggestions;
  const handleClick = async () => {
    await workflowRequest(
      new RunWorkflowRequest({
        workflowName: "process-phone-call",
        arguments: [],
        entityType: SuggestionEntityType.PHONECALL,
        entityRef: phoneCall.ref,
      }),
    );
  };

  const hasSummary = !!suggestions.summary?.content;
  const hasOtherSuggestions =
    suggestions.tasks.length > 0 ||
    suggestions.facts.length > 0 ||
    suggestions.events.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap="24px" width="100%">
      {delegationTaskRef && (
        <DelegationTaskDetails delegationTaskRef={delegationTaskRef} />
      )}
      {phoneCall.state === PhoneCall_State.ACTIVE && (
        <Typography variant="bodySmall">
          Summary and task suggestions will be generated at the end of the call.{" "}
          <span
            style={{
              color: "#198282",
              fontWeight: "600",
              cursor: "pointer",
            }}
            onClick={handleClick}
          >
            Try now?
          </span>
        </Typography>
      )}
      {(loading || isFreshlyCompleteWithoutSuggestions) && <Loader />}
      {!loading && hasSummary && (
        <SummarySection
          summary={suggestions.summary!}
          notesEditorRef={notesEditorRef}
          isNotesEmpty={isNotesEmpty}
        />
      )}
      {!loading && hasOtherSuggestions && (
        <SuggestionsList
          inlineMode={true}
          suggestions={suggestions}
          actions={{
            suggestTask: (t) => actionsRef.current?.suggestTask(t),
            suggestFact: (f) => actionsRef.current?.suggestFact(f),
            suggestEvent: (e) => actionsRef.current?.suggestEvent(e),
          }}
          isOpen={true}
          entityRef={phoneCall.ref}
          entityType={"phonecall"}
          familyRef={familyRef}
          hideSummary={true}
        />
      )}
      {suggestions.summary?.content ===
        "Transcript error, please try again." && (
        <span
          style={{
            color: "#198282",
            fontWeight: "600",
            cursor: "pointer",
          }}
          onClick={handleClick}
        >
          Try again?
        </span>
      )}
      <SuggestionActions ref={actionsRef} familyRef={familyRef} />
    </Box>
  );
};

type Props = {
  phoneCall: PhoneCall;
  recipient: AccountStub;
  callNotes: Note;
  familyRef: string;
  suggestions: Suggestions;
  copyToOptions: JSX.Element[];
  onCopyToHandler: () => void;
  delegationTaskRef?: string;
};

export default ({
  phoneCall,
  callNotes,
  familyRef,
  suggestions,
  copyToOptions,
  onCopyToHandler,
  delegationTaskRef,
}: Props) => {
  const [copyPopperOpen, setCopyPopperOpen] = useState(false);
  const notesEditorRef = useRef<NotesEditorHandle | null>(null);

  const isNotesEmpty = !callNotes.content?.textContent?.trim();

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"32px"}
      marginTop={"32px"}
      paddingRight="20px"
      paddingLeft="20px"
    >
      <SuggestionsSection
        phoneCall={phoneCall}
        suggestions={suggestions}
        familyRef={familyRef}
        title={"Smart Suggestions"}
        delegationTaskRef={delegationTaskRef}
        notesEditorRef={notesEditorRef}
        isNotesEmpty={isNotesEmpty}
      />
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"12px"}
        marginBottom={"32px"}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4">Notes</Typography>
          {!delegationTaskRef && (
            <CopyToButton
              menuOptions={copyToOptions}
              isOpen={copyPopperOpen}
              setIsOpen={setCopyPopperOpen}
              onClickHandler={onCopyToHandler}
              content={"Add to task"}
            />
          )}
        </Box>
        <NotesEditor
          ref={notesEditorRef}
          note={callNotes}
          title={"Notes"}
          initialContent={""}
          style={{ border: "none", padding: "0", boxShadow: "none" }}
          editorType="simple"
        />
      </Box>
    </Box>
  );
};
